// components/flow/components/config/PhotoConfig.tsx
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Image as ImageIcon, MessageCircle, Square, RectangleHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { useToast } from '@/hooks/use-toast.ts';
import { updateNode } from '@/services/api';
import type { FlowNode, ImageAspectRatio } from '@/types/flow.ts';
import MediaLibraryModal from '../MediaLibraryModal.tsx';
import { aspectClass, detectAspectFromUrl } from '@/lib/image-aspect';
import RandomNextToggle from './shared/RandomNextToggle';

interface PhotoConfigProps {
    node: FlowNode;
    onClose: () => void;
    pageId: string;
}

export default function PhotoConfig({ node, onClose, pageId }: PhotoConfigProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [nodeName, setNodeName] = useState(node.data.node_name || '');
    const [mediaUrl, setMediaUrl] = useState(node.data.config.media_url || '');
    const [mediaFilename, setMediaFilename] = useState(node.data.config.media_filename || '');
    const [typingSeconds, setTypingSeconds] = useState(node.data.config?.delay_seconds || 0);
    const [imageAspectRatio, setImageAspectRatio] = useState<ImageAspectRatio>(
        (node.data.config?.image_aspect_ratio as ImageAspectRatio) || 'horizontal'
    );
    const [isRandom, setIsRandom] = useState<boolean>(node.data.is_random ?? false);

    const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);

    // Mutation para atualizar node
    const updateMutation = useMutation({
        mutationFn: (data: any) => updateNode(node.data.id, data),
        onSuccess: () => {
            toast({
                title: 'Configurações salvas!',
                description: 'A foto foi atualizada com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onClose();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Handler de salvar
    const handleSave = () => {
        if (!mediaUrl) {
            toast({
                variant: 'destructive',
                title: 'Imagem obrigatória',
                description: 'Por favor, selecione uma imagem.',
            });
            return;
        }

        updateMutation.mutate({
            nome: nodeName,
            is_random: isRandom,
            media_url: mediaUrl,
            media_filename: mediaFilename,
            media_type: 'image',
            image_aspect_ratio: imageAspectRatio,
            delay_seconds: typingSeconds || null,
            page_id: pageId
        });
    };

    // Handler de seleção de mídia — auto-detecta aspect pelas dimensões
    const handleMediaSelect = async (media: any) => {
        setMediaUrl(media.file_url);
        setMediaFilename(media.filename);
        if (media.file_url) {
            try {
                const detected = await detectAspectFromUrl(media.file_url);
                setImageAspectRatio(detected);
            } catch { /* mantém o atual */ }
        }
    };

    return (
        <>
            <div className="flex-1 overflow-auto">
                <div className="p-6 space-y-6">
                    {/* Nome do Node */}
                    <div>
                        <Label htmlFor="node-name">Nome do Node (opcional)</Label>
                        <Input
                            id="node-name"
                            value={nodeName}
                            onChange={(e) => setNodeName(e.target.value)}
                            placeholder="Enviar Foto"
                            className="mt-1.5"
                            disabled={updateMutation.isPending}
                        />
                    </div>

                    {/* Imagem */}
                    <div>
                        <Label>Imagem *</Label>
                        {mediaUrl ? (
                            <div className="mt-1.5 space-y-3">
                                <div className={`relative ${aspectClass(imageAspectRatio)} bg-gray-100 rounded-lg overflow-hidden border border-gray-200`}>
                                    <img
                                        src={mediaUrl}
                                        alt="Preview"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <span className="text-sm text-gray-700 truncate flex-1">
                                        {mediaFilename}
                                    </span>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setIsMediaLibraryOpen(true)}
                                        disabled={updateMutation.isPending}
                                    >
                                        Trocar
                                    </Button>
                                </div>

                                {/* Formato */}
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Formato da imagem
                                    </Label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {(['horizontal', 'square'] as ImageAspectRatio[]).map((opt) => {
                                            const active = imageAspectRatio === opt;
                                            const Icon = opt === 'horizontal' ? RectangleHorizontal : Square;
                                            return (
                                                <button
                                                    key={opt}
                                                    type="button"
                                                    onClick={() => setImageAspectRatio(opt)}
                                                    className={`flex items-center gap-2 p-2 rounded-lg border-2 text-sm transition-all ${
                                                        active
                                                            ? 'border-indigo-500 bg-indigo-50'
                                                            : 'border-gray-200 hover:border-gray-300'
                                                    }`}
                                                >
                                                    <Icon className="h-4 w-4 text-gray-500" />
                                                    <span className="text-xs">
                                                        {opt === 'horizontal' ? 'Horizontal' : 'Quadrado'}
                                                        <span className="text-gray-400 ml-1">
                                                            {opt === 'horizontal' ? '1.91:1' : '1:1'}
                                                        </span>
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => setIsMediaLibraryOpen(true)}
                                disabled={updateMutation.isPending}
                                className={`mt-1.5 w-full ${aspectClass(imageAspectRatio)} bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-colors flex flex-col items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                <ImageIcon className="w-12 h-12 text-gray-400" />
                                <span className="text-sm text-gray-600">Clique para selecionar imagem</span>
                            </button>
                        )}
                    </div>

                    {/* Digitando */}
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                            <MessageCircle className="w-3.5 h-3.5" />
                            Digitando... (segundos)
                        </Label>
                        <Input
                            type="number"
                            min={0}
                            max={30}
                            value={typingSeconds}
                            onChange={(e) => setTypingSeconds(Math.max(0, Math.min(30, parseInt(e.target.value) || 0)))}
                            placeholder="0"
                            className="h-10 w-32"
                            disabled={updateMutation.isPending}
                        />
                        <p className="text-xs text-gray-500">
                            Mostra "digitando..." por X segundos antes de enviar. 0 = desativado.
                        </p>
                    </div>

                    <RandomNextToggle
                        isRandom={isRandom}
                        onChange={setIsRandom}
                        disabled={updateMutation.isPending}
                    />

                    {/* Info Helper */}
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                        <p className="text-xs text-indigo-900">
                            💡 <strong>Dica:</strong> Esta foto será enviada diretamente para o usuário sem botões ou texto adicional.
                        </p>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                <Button variant="outline" onClick={onClose} disabled={updateMutation.isPending}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                >
                    {updateMutation.isPending ? (
                        'Salvando...'
                    ) : (
                        <>
                            <Save className="w-4 h-4 mr-2" />
                            Salvar Configurações
                        </>
                    )}
                </Button>
            </div>

            {/* Media Library Modal */}
            <MediaLibraryModal
                isOpen={isMediaLibraryOpen}
                onClose={() => setIsMediaLibraryOpen(false)}
                onSelect={handleMediaSelect}
                pageId={pageId}
                mediaType="image"
            />
        </>
    );
}